import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TableContainer,
  Paper,
} from '@mui/material';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableItem } from './sortable-item.jsx';

const OrderableTable = ({
  columns = [],
  data = [],
  rowKey = 'id',
  onChangeOrder,
  onSaveOrderChange,
}) => {
  const [items, setItems] = useState(data);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    setItems(data);
  }, [data]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const activeItem = items.find((item) => item[rowKey] === active.id);
    const overItem = items.find((item) => item[rowKey] === over.id);

    // Se qualquer um dos itens não for ativo, não faz nada
    if (!activeItem?.active || !overItem?.active) return;

    const oldIndex = items.filter((i) => i.active).findIndex((item) => item[rowKey] === active.id);
    const newIndex = items.filter((i) => i.active).findIndex((item) => item[rowKey] === over.id);

    const newItems = arrayMove(
      items.filter((i) => i.active),
      oldIndex,
      newIndex,
    );

    // Substitui os itens ativos reordenados na lista completa
    const reordered = [...newItems, ...items.filter((i) => !i.active)];

    setItems(reordered);
    onChangeOrder?.(oldIndex, newIndex);
  };

  return (
    <div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={items.filter((item) => item.active).map((item) => item[rowKey])}
          strategy={verticalListSortingStrategy}
        >
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell key={col.key}>{col.title}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => (
                  <SortableItem key={row[rowKey]} id={row[rowKey]}>
                    {columns.map((col) => (
                      <TableCell key={col.key}>
                        {col.render ? col.render(row) : row[col.key]}
                      </TableCell>
                    ))}
                  </SortableItem>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SortableContext>
      </DndContext>
      <Button
        variant="contained"
        color="primary"
        onClick={() => onSaveOrderChange?.(items)}
        sx={{ mt: 2 }}
      >
        Salvar a alteração de ordem
      </Button>
    </div>
  );
};
OrderableTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      render: PropTypes.func,
    }),
  ).isRequired,
  data: PropTypes.array,
  rowKey: PropTypes.string,
  onChangeOrder: PropTypes.func,
  onSaveOrderChange: PropTypes.func,
};

export default OrderableTable;
